import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Activity, BarChart3, ShieldCheck, Sparkles, TrendingUp, Zap,
  ArrowRight, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { Logo } from "../components/Logo";

// 2026-08 — migrated off Emergent's managed OAuth proxy to direct Google
// sign-in (docs/EMERGENT_DEPENDENCY_AUDIT.md, item 1). When
// REACT_APP_GOOGLE_CLIENT_ID is configured, redirect straight to Google;
// the callback is handled server-side by GET /api/auth/google/callback,
// which sets the session cookie and redirects to /dashboard directly (no
// frontend AuthCallback round-trip needed for this path). If the client ID
// isn't set yet (e.g. mid-migration, before Google Cloud credentials are
// provisioned), fall back to the old Emergent-proxy flow so login keeps
// working — see auth_service.py's POST /auth/session docstring.
const startGoogleSignIn = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
    return;
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const redirectUri = backendUrl.replace(/\/$/, "") + "/api/auth/google/callback";
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid email profile",
    access_type: "online",
    prompt: "select_account",
  });
  window.location.href = "https://accounts.google.com/o/oauth2/v2/auth?" + params.toString();
};

const Feature = ({ icon: Icon, title, body }) => (
  <div className="border border-grid p-5 space-y-2" data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <Icon size={18} style={{ color: "var(--bull)" }} />
    <h3 className="font-heading text-lg">{title}</h3>
    <p className="text-xs text-muted-2 leading-relaxed">{body}</p>
  </div>
);

const PricingCard = ({ name, blurb, features, cta, testid, highlight }) => (
  <div
    className={`border p-6 space-y-4 ${highlight ? "border-2" : "border"}`}
    style={{ borderColor: highlight ? "var(--bull)" : "var(--grid)" }}
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <h3 className="font-heading text-xl">{name}</h3>
      {highlight && (
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono-data px-2 py-0.5"
              style={{ color: "var(--bull)", borderColor: "var(--bull)", border: "1px solid" }}>
          Most Popular
        </span>
      )}
    </div>
    <p className="text-xs text-muted-2">{blurb}</p>
    <div className="text-3xl font-heading font-bold">
      Free <span className="text-xs text-muted-3 font-normal">while in beta</span>
    </div>
    <ul className="space-y-2 text-xs">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2">
          <CheckCircle2 size={14} style={{ color: "var(--bull)" }} className="mt-0.5 flex-shrink-0" />
          <span className="text-muted-1">{f}</span>
        </li>
      ))}
    </ul>
    <button
      onClick={cta}
      className="w-full px-4 py-3 border text-[11px] uppercase tracking-[0.2em] font-mono-data hover:bg-white/[0.04]"
      style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
      data-testid={`${testid}-cta`}
    >
      Get Started →
    </button>
  </div>
);

const Faq = ({ q, a }) => (
  <details className="border border-grid p-4 group">
    <summary className="cursor-pointer text-sm font-medium flex items-center justify-between">
      <span>{q}</span>
      <span className="text-muted-3 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
    </summary>
    <p className="text-xs text-muted-2 mt-3 leading-relaxed">{a}</p>
  </details>
);

export const Landing = () => {
  const { user, isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const [errorBanner, setErrorBanner] = useState(params.get("auth_error"));
  useEffect(() => {
    if (errorBanner) {
      const t = setTimeout(() => setErrorBanner(null), 8000);
      return () => clearTimeout(t);
    }
  }, [errorBanner]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      {/* Top bar */}
      <header className="border-b border-grid">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={22} />
            <span className="font-heading text-lg tracking-tight">StoxTrade</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data ml-1">
              Beta
            </span>
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="text-[11px] uppercase tracking-[0.2em] font-mono-data px-3 py-1.5 border border-grid hover:bg-white/[0.04]"
                data-testid="landing-go-to-dashboard"
              >
                Open Dashboard →
              </Link>
            ) : (
              <button
                onClick={startGoogleSignIn}
                className="text-[11px] uppercase tracking-[0.2em] font-mono-data px-4 py-1.5 border"
                style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
                data-testid="landing-signin-top"
              >
                Sign in with Google
              </button>
            )}
          </div>
        </div>
      </header>

      {errorBanner && (
        <div className="bg-red-500/10 border-b border-red-500/40 px-4 py-2 text-xs text-red-300 text-center flex items-center justify-center gap-2"
             data-testid="landing-auth-error-banner">
          <AlertTriangle size={14} /> {errorBanner}
        </div>
      )}

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
              Indian Markets · Algorithmic Trading · Built for the F&O Trader
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Spot the setup.<br />
              Trade with conviction.<br />
              <span style={{ color: "var(--bull)" }}>Skip the noise.</span>
            </h1>
            <p className="text-base text-muted-2 max-w-2xl leading-relaxed">
              StoxTrade scans the F&amp;O universe every 5 minutes for high-probability VWAP bounces,
              candle breakouts, and OI long-buildup setups. Real Upstox order routing,
              hard R:R gates, and a paper-mode safety net so you can learn the system risk-free.
            </p>
            <div className="flex flex-wrap gap-3">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="px-6 py-3 border text-[11px] uppercase tracking-[0.2em] font-mono-data"
                  style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
                  data-testid="landing-hero-dashboard"
                >
                  Open Dashboard →
                </Link>
              ) : (
                <button
                  onClick={startGoogleSignIn}
                  className="px-6 py-3 border text-[11px] uppercase tracking-[0.2em] font-mono-data inline-flex items-center gap-2"
                  style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
                  data-testid="landing-hero-signin"
                >
                  Start Free with Google <ArrowRight size={14} />
                </button>
              )}
              <a
                href="#pricing"
                className="px-6 py-3 border border-grid text-[11px] uppercase tracking-[0.2em] font-mono-data hover:bg-white/[0.04]"
              >
                See Pricing
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-6 pt-4 text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
              <span className="flex items-center gap-1.5"><ShieldCheck size={12} /> SEBI-aware risk gates</span>
              <span className="flex items-center gap-1.5"><Activity size={12} /> Live Upstox feed</span>
              <span className="flex items-center gap-1.5"><Zap size={12} /> 5-min auto-scan</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-grid p-5 space-y-3 font-mono-data text-[11px]">
              <div className="flex items-center justify-between text-muted-3">
                <span>VWAP Bounce · live · 10:25 IST</span>
                <span style={{ color: "var(--bull)" }}>● scanning F&amp;O universe</span>
              </div>
              <div className="border border-grid p-3 space-y-2">
                <div className="flex justify-between"><span>RELIANCE</span><span>2-STRONG · ATR-SL ₹4.20</span></div>
                <div className="flex justify-between"><span>ASIANPAINT</span><span>PULLBACK · Target +8.0%</span></div>
                <div className="flex justify-between text-muted-3"><span>504 stocks scanned</span><span>2 auto-entered</span></div>
              </div>
              <div className="text-[10px] text-muted-3">
                Every rejection is logged — you see <em>why</em> the system stayed flat,
                not just blank space.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-grid bg-white/[0.01]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 space-y-10">
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
              The Engine
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
              Built around one idea: stay flat unless the trade is obvious.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Feature icon={Activity} title="Live scanners" body="Sections 5 (Candle Breakout), 6 (OI Long Buildup), 8 (VWAP Bounce) — auto-firing every 5 min on the F&amp;O universe (~210 stocks)." />
            <Feature icon={ShieldCheck} title="Hard risk gates" body="R:R floor of 0.8, max 50% price move from baseline, contract quality + spread checks. No 'I'll just take this one' trades." />
            <Feature icon={BarChart3} title="Paper before live" body="Every signal can be paper-traded first. Switch to live order routing only after you've watched the system perform." />
            <Feature icon={TrendingUp} title="Dynamic targets" body="ATR-based stops, sector + sentiment-aware target bands (6–12% on VWAP Bounce, capped at 10% on Candle Breakout)." />
            <Feature icon={Zap} title="Telegram alerts" body="Section-by-section pause toggles. Get pinged only for the strategies you care about, only in the mode you've opted into." />
            <Feature icon={Sparkles} title="Bring your own Upstox" body="Connect your personal Upstox account, route orders through your own broker. We never hold your funds or credentials offline." />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 space-y-10">
        <div className="text-center space-y-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
            Pricing
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            Free during beta · Subscription tiers coming soon
          </h2>
          <p className="text-sm text-muted-2 max-w-2xl mx-auto">
            All accounts get admin-approved access during the beta. Pricing locks in
            when payments go live — beta users keep their tier for the first 3 months.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          <PricingCard
            name="Tips Subscription"
            blurb="Stock tips, market insights, manual trading references. No algorithmic execution."
            features={[
              "Sections 1–4: Market Sentiment, OI Suggestions, Watchlist, Sniper Trades",
              "Educational content + commentary",
              "Telegram alerts (opt-in)",
              "Read-only access to algo data",
            ]}
            cta={isAuthenticated ? () => (window.location.href = "/dashboard") : startGoogleSignIn}
            testid="pricing-tips"
          />
          <PricingCard
            name="Auto Trading"
            highlight
            blurb="The full algorithmic engine. Auto-picks, paper + live trading, all strategies."
            features={[
              "Everything in Tips, plus:",
              "Candle Breakout (auto)",
              "OI Long Buildup (auto)",
              "VWAP Bounce (auto)",
              "Live Upstox order routing",
              "Per-strategy Telegram + paper toggles",
            ]}
            cta={isAuthenticated ? () => (window.location.href = "/dashboard") : startGoogleSignIn}
            testid="pricing-auto"
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-grid bg-white/[0.01]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 space-y-6">
          <div className="space-y-2 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
              FAQ
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
              The honest answers
            </h2>
          </div>
          <div className="space-y-3">
            <Faq q="Do you place orders with my money automatically?" a="Only when you've explicitly enabled Live Trading for your account AND connected your own Upstox API key. Default mode is Paper — every signal is simulated until you flip the toggle. The system also has hard R:R, price-move and contract-quality gates that can veto any auto-entry regardless of mode." />
            <Faq q="Why does my account need admin approval?" a="StoxTrade is in private beta. To keep capacity sustainable and protect the live trading queue, every new sign-up is queued for admin review. We typically approve within 24 hours. Power Users (internal/trusted) bypass the wait." />
            <Faq q="What does 'free during beta' actually mean?" a="Right now subscriptions are admin-assigned and free. When we open paid tiers, beta users keep their assigned tier (Tips or Auto Trading) free for the first 3 months. No card on file required today." />
            <Faq q="Is this SEBI-registered investment advice?" a="No. StoxTrade is a scanning + execution toolkit, not an RA / RIA. Every signal is information, not advice. You're responsible for your own trading decisions and capital." />
            <Faq q="What happens to my data if I leave?" a="Soft-delete: your trade history, settings and audit logs stay in our system for our own compliance + reporting. Active sessions are terminated immediately, live + auto trading is stopped, API access is blocked. Hard delete on request." />
          </div>
        </div>
      </section>

      <footer className="border-t border-grid">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
          <span>StoxTrade © {new Date().getFullYear()}</span>
          <span>Built for Indian F&O traders · Beta</span>
        </div>
      </footer>
    </div>
  );
};
